import { useCallback } from "react"
import type { DateTime } from "luxon"
import { download, generateCsv, mkConfig } from "export-to-csv"

import { toCsvText } from "../../../../../../lib/stringLib"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { useDateTime } from "../../../../../localization/useDateTime"

import {
    formatCardNumber,
    getIdCardHolderLabel,
    ID_CARD_STATUS_L10N_BASE,
    ID_CARD_TYPE_L10N_BASE,
} from "../idCardsListHelpers"
import type { IdCardRow } from "../idCardsListTypes"

type ExportParams = Readonly<{
    startDate: DateTime
    endDate: DateTime
}>

export function useExportIdCardsList({ startDate, endDate }: ExportParams) {
    const dateTimeFactory = useDateTime()
    const { l10n } = useAppLocalization()

    return useCallback(
        (rows: ReadonlyArray<IdCardRow>) => {
            const csvOptions = mkConfig({
                fieldSeparator: ";",
                quoteStrings: true,
                decimalSeparator: ",",
                useBom: true,
                useKeysAsHeaders: true,
                filename: `Facilitynet-id-cards-${dateTimeFactory.formatDate(startDate)}_${dateTimeFactory.formatDate(endDate)}`,
            })

            const formatApiDate = (value: string | undefined) =>
                value ? dateTimeFactory.formatDate(dateTimeFactory.fromApi(value)) : ""

            const csvRows = rows.map((row) => ({
                [l10n.getString("merchant-id-cards-table-card-number")]: formatCardNumber(row.cardNumber),
                [l10n.getString("merchant-id-cards-table-card-type")]: l10n.getStringForEnum(
                    ID_CARD_TYPE_L10N_BASE,
                    row.cardType,
                    row.cardType,
                ),
                [l10n.getString("merchant-id-cards-table-company")]: toCsvText(row.company?.name ?? ""),
                [l10n.getString("merchant-id-cards-list-export-batch")]: toCsvText(row.batch ?? ""),
                [l10n.getString("merchant-id-cards-list-export-created")]: formatApiDate(row.created),
                [l10n.getString("merchant-id-cards-table-status")]: l10n.getStringForEnum(
                    ID_CARD_STATUS_L10N_BASE,
                    row.status,
                    row.status,
                ),
                [l10n.getString("merchant-id-cards-list-export-cardholder")]: toCsvText(
                    row.holder ? getIdCardHolderLabel(row.holder) : "",
                ),
                [l10n.getString("merchant-id-cards-list-export-assigned-on")]: formatApiDate(row.assignedOn),
                [l10n.getString("merchant-id-cards-list-export-valid-from")]: formatApiDate(row.validFrom),
                [l10n.getString("merchant-id-cards-list-export-valid-to")]: formatApiDate(row.validTo),
                [l10n.getString("merchant-id-cards-list-export-scans-per-day")]:
                    row.scansPerDay ?? l10n.getString("merchant-id-cards-list-export-scans-unlimited"),
            }))

            download(csvOptions)(generateCsv(csvOptions)(csvRows))
        },
        [dateTimeFactory, l10n, startDate, endDate],
    )
}
